import { useEffect, useRef, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";

import { PaginationDots } from "features/components/base/public/HOC/Pagination";
import { MediaThumbnailsSync } from "shared/video-thumbnail/MediaThumbnail";
import { downArrowIcon, minusIcon, playIcon, plusIcon, upArrowIcon } from "shared/assets/icons";

import './ItemGalleryHOC.scss';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

const VideoPlayer = ({ url, autoPlay=true }) => {
    return (
        //poster={currentThumbnailArray[0]}
        <video preload="none" autoPlay={autoPlay} controls disablePictureInPicture controlsList="nodownload" webkit-playsinline playsinline >
            <source src={url}></source>
            Your browser does not support HTML video.
        </video>
    )
};

const ImageZoomDragger = ({ url, modalRef }) => {

    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const start = useRef({ x: 0, y: 0 });

    const [isDragging, setIsDragging] = useState(false);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const handleMouseDown = (event) => {
        event.preventDefault();

        // If the zoom is 1, don't need
        if ( scale == MIN_ZOOM ) return;

        setIsDragging(true);
        start.current = { x: event.clientX - position.x, y: event.clientY - position.y };
    };

    const handleReset = (event) => {
        event.preventDefault();
        setIsDragging(false);
        start.current = { x: 0, y: 0 };
    };

    const handleMouseMove = (event) => {
        if ( !isDragging ) return;

        // TODO check if the image is going beyond the boundaries
        // const rect = event.currentTarget.getBoundingClientRect()
        setPosition({x: event.clientX - start.current.x, y: event.clientY - start.current.y});
    };

    const handleOnZoom = (event, isZoomClicked=false) => {
        let zoom = scale;
        if ( event ) {
            zoom = Number(event.target.value);
        } else if ( isZoomClicked ) {
            zoom = (scale + 1) > MAX_ZOOM ? MAX_ZOOM : (scale + 1);
        } else if ( !isZoomClicked ) {
            zoom = (scale - 1) < MIN_ZOOM ? MIN_ZOOM : (scale - 1);
        }
        
        if ( zoom === 1 ) {
            start.current = { x: 0, y: 0 };
            setPosition({ x: 0, y: 0 });
        }
        setScale(zoom);
    };

    const handleTouchStart = (event) => {
        // If the zoom is 1, don't need
        if ( scale == MIN_ZOOM ) return;

        if (event.touches.length === 1) {
            const touch = event.touches[0];
            setIsDragging(true);
            start.current = { x: touch.clientX - position.x, y: touch.clientY - position.y }
        }
    };

    const handleTouchMove = (event) => {
        if (!isDragging || event.touches.length !== 1) return;

        // While touch moving we don't need modal scroll
        if ( modalRef?.current?.dialog ) {
            modalRef.current.dialog.classList.add('disable-scroll');
        }

        const touch = event.touches[0];
        setPosition({ x: touch.clientX - start.current.x, y: touch.clientY - start.current.y });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        start.current = { x: 0, y: 0 };
        if (modalRef?.current?.dialog) {
            modalRef.current.dialog.classList.remove('disable-scroll');
        }
    };

    // const clampPosition = (x, y) => {
    //     const container = containerRef.current;
    //     const image = imageRef.current;
    //     if (!container || !image) return { x, y };

    //     const containerRect = container.getBoundingClientRect();
    //     const imageWidth = image.naturalWidth * scale;
    //     const imageHeight = image.naturalHeight * scale;

    //     const minX = Math.min(0, containerRect.width - imageWidth);
    //     const minY = Math.min(0, containerRect.height - imageHeight);
    //     const maxX = 0;
    //     const maxY = 0;

    //     return {
    //         x: Math.max(minX, Math.min(x, maxX)),
    //         y: Math.max(minY, Math.min(y, maxY)),
    //     };
    // };

    return (
        <div className="zoom-dragger-container">
            <div ref={containerRef}
                className={`zoom-dragger ${isDragging ? "dragging" : scale > MIN_ZOOM ? "draggable" : ""}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleReset}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseOut={handleReset} >
                <img ref={imageRef}
                    src={url}
                    className={`media-image`}
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                        transition: isDragging ? 'none' : 'transform 0.25s ease-out'
                    }}
                    alt="item-display-image"
                    loading="lazy"
                    draggable={false} />
            </div>
            <div className="zoom-slider-container">
                <div className={scale > MIN_ZOOM ? 'active' : 'inactive'} onClick={scale > MIN_ZOOM ? () => { handleOnZoom(null) } : null}>
                    {minusIcon}
                </div>
                <input
                    className="zoom-slider"
                    type="range"
                    min={MIN_ZOOM}
                    max={MAX_ZOOM}
                    step="0.1"
                    value={scale}
                    onChange={handleOnZoom}
                />
                <div className={scale < MAX_ZOOM ? 'active' : 'inactive'} onClick={scale < MAX_ZOOM ? () => { handleOnZoom(null, true) } : null}>
                    {plusIcon}
                </div>
            </div>
        </div>
    );
};

export const ItemGalleryHOC = ({ list = [], galleryInModal = false, modalRef=null, activeIndex=0, onChangeMedia }) => {

    const imageScrollRef = useRef(null);
    const horizontalScrollRef = useRef(null);

    const magnifier = useRef(null);
    const magnifierImage = useRef(null);

    const [activeImage, setActiveImage] = useState(list.length > 0 ? list[activeIndex] : {});
    const [videos, setVideos] = useState([]);
    const [videoThumbnails, setVideoThumbnails] = useState({});
    const [magnifierHide, setMagnifierHide] = useState(true);
    const [videoPlayerModal, setVideoPlayerModal] = useState(false);

    useEffect(() => {
        setActiveImage(list.length > 0 ? list[activeIndex] : {});
        setVideos(list.filter(asset => asset.type === 'video'));
      return () => {
      }
    }, [list]);

    const handleScroll = (direction) => {
        /* Scroll up or down with animation */
        direction === 'Down' ? imageScrollRef.current.scrollTo({
            top: (imageScrollRef.current.scrollTop + 150),
            behavior: 'smooth'
        }) : imageScrollRef.current.scrollTo({
            top: (imageScrollRef.current.scrollTop - 150),
            behavior: 'smooth'
        });
    };

    const moveMagnifier = (e) => {
        const isdefaultImage = Array.isArray(list) && list.length == 1 && !list[0].id;
        if (!isdefaultImage) {
            let pos, x, y;
            const zoom = 4;

            const img = magnifierImage.current;
            const glass = magnifier.current;

            glass.style.backgroundSize = (img.width * zoom) + "px " + (img.height * zoom) + "px";

            const bw = 3;
            const w = glass.offsetWidth / 2;
            const h = glass.offsetHeight / 2;

            /*prevent any other actions that may occur when moving over the image*/
            e.preventDefault();

            /*get the cursor's x and y positions:*/
            pos = getCursorPos(e);
            x = pos.x;
            y = pos.y;

            /*prevent the magnifier glass from being positioned outside the image:*/
            if (x > img.width - (w / zoom)) { x = img.width - (w / zoom); }
            if (x < w / zoom) { x = w / zoom; }
            if (y > img.height - (h / zoom)) { y = img.height - (h / zoom); }
            if (y < h / zoom) { y = h / zoom; }

            /*set the position of the magnifier glass:*/
            glass.style.left = (x - w) + "px";
            glass.style.top = (y - h) + "px";

            /*display what the magnifier glass "sees":*/
            glass.style.backgroundPosition = "-" + ((x * zoom) - w + bw) + "px -" + ((y * zoom) - h + bw) + "px";
        }
    };

    const getCursorPos = (e) => {
        let a, x = 0, y = 0;

        const img = magnifierImage.current;

        e = e || window.event;

        /*get the x and y positions of the image:*/
        a = img.getBoundingClientRect();

        /*calculate the cursor's x and y coordinates, relative to the image:*/
        x = e.pageX - a.left;
        y = e.pageY - a.top;

        /*consider any page scrolling:*/
        x = x - window.pageXOffset;
        y = y - window.pageYOffset;
        return { x: x, y: y };
    };

    const toggleMagnifier = (event, value, flag) => {
        if (flag === 'enter' && !magnifierHide) {
            setMagnifierHide(value);
        } else {
            setMagnifierHide(value);
        }
    };

    const onImageClick = (media, index) => {
        setActiveImage(media);
        onChangeMedia && onChangeMedia(index);
    };

    return (
        <div className="media-container">
            <MediaThumbnailsSync videos={videos} thumbnailCallback={(data) => {
                setVideoThumbnails(prevState => {
                    return {...prevState, ...data};
                });
            }} />
            <div className="media-web">
                <div className="media-selection-container">

                    {/** Media scroll up */}
                    <div className={`scroll-up ${list.length >= 5 ? 'clickable' : 'disabled'}`}  onClick={() => handleScroll('Up')}>
                        {upArrowIcon}
                    </div>

                    {/** List of all images */}
                    <div className="all-images" ref={imageScrollRef}>
                        {
                            list.map((media, index) =>
                                <div key={`image_${index}`} className={`select-container clickable d-flex align-items-center justify-content-center ${activeImage.id === media.id ? 'active' : ''}`} style={{ backgroundColor: media.type === 'video' && !videoThumbnails[media.id] ? "#392E2E" : 'transparent' }} onClick={() => { onImageClick(media, index); }}>
                                    {
                                        media.type === 'image' ?
                                            <img src={media.thumbnailurl ? media.thumbnailurl : media.url} alt={`item-image-${index}`} /> :
                                            media.id in videoThumbnails ? 
                                            <div className="media-thumbnail">
                                                <img className="thumbnail-image" src={videoThumbnails[media.id]} />
                                                <div className="play-icon" >
                                                    {playIcon}
                                                </div>
                                            </div> : null
                                    }
                                </div>
                            )
                        }
                    </div>

                    {/** Media scroll down */}
                    <div className={`scroll-down  ${list.length >= 5 ? 'clickable' : 'disabled'}`} onClick={() => handleScroll('Down')}>
                        {downArrowIcon}
                    </div>
                </div>

                {/** Media display area */}
                <div className={`media-display-area ${Array.isArray(list) && list.length == 0 ? 'white-bg' : ''}`}>
                    {
                        activeImage.type === "image" ?
                            galleryInModal ? 
                                <ImageZoomDragger key={activeImage.url} url={activeImage.url} modalRef={modalRef} /> :
                                <div className={`img-magnifier-container d-flex align-items-center justify-content-center`}>
                                    {
                                        Array.isArray(list) && list.length == 1 && !list[0].id ? null :
                                        <div className={`img-magnifier-glass ${magnifierHide ? 'invisible' : ''}`}
                                            style={{ backgroundImage: `url(${activeImage.url})` }}
                                            ref={magnifier}
                                            onMouseMove={moveMagnifier}
                                            onMouseOut={(event) => toggleMagnifier(event, true, 'leave')}></div>
                                    }
                                    <img src={activeImage.url}
                                        loading="lazy"
                                        alt="item-image"
                                        className={`media-image`}
                                        ref={magnifierImage}
                                        onMouseMove={moveMagnifier}
                                        onMouseEnter={(event) => toggleMagnifier(event, false, 'enter')} />
                                </div>
                            :
                            galleryInModal ?
                                <video key={activeImage.url} preload="none" autoPlay={true} controls disablePictureInPicture controlsList="nodownload" webkit-playsinline playsinline >
                                    <source src={activeImage.url}></source>
                                    Your browser does not support HTML video.
                                </video> :
                                activeImage.id in videoThumbnails ?
                                    <div className="position-relative d-flex align-items-center justify-content-center">
                                        <img src={videoThumbnails[activeImage.id]} alt="thumbnail-new" className="thumbnail-img"></img>
                                        <div className="position-absolute z-1 clickable" onClick={() => { setVideoPlayerModal(true); }}>
                                            {playIcon}
                                        </div>
                                    </div> : null
                    }
                </div>
            </div> 
            <div className="media-mobile">
                <div className={`media-display-area ${Array.isArray(list) && list.length == 0 ? 'white-bg' : ''}`}>
                    {
                        Array.isArray(list) && list.length > 0 ?
                        <Row className="horizontal-row" xs={1} ref={horizontalScrollRef}>
                            {
                                list.map((asset, index) => (
                                    <Col className="horizontal-col" >
                                        {
                                            asset.type === "image" ? 
                                            <img src={asset.url} alt="item-image" className={`media-image ${Array.isArray(list) && list.length == 0 ? 'no-image-css' : ''}`} /> :
                                            asset.id in videoThumbnails ? 
                                            <div className="media-thumbnail" onClick={() => {
                                                setActiveImage(asset);
                                                setVideoPlayerModal(true);
                                                }}>
                                                <img className="thumbnail-image" src={videoThumbnails[asset.id]} />
                                                <div className="play-icon" >
                                                    {playIcon}
                                                </div>
                                            </div> : null
                                        }
                                    </Col>
                                ))
                            }
                        </Row> : null
                    }
                </div>
                <PaginationDots scrollRef={horizontalScrollRef} loading={false} totalSize={list.length} pageSize={1} />
            </div>

            <Modal centered show={videoPlayerModal} animation={false} size="lg" onHide={() => setVideoPlayerModal(false)}>
            <div type="button" onClick={() => setVideoPlayerModal(false)} className="close" style={{fontSize:'2rem', paddingRight: '1rem'}} data-dismiss="modal" variant="outline-primary" aria-label="Close"><span aria-hidden="true">&times;</span></div>

                <Modal.Body className="media-display-area-modal d-flex flex-column align-items-center justify-content-center">
                    {
                        videoPlayerModal ? 
                        <VideoPlayer url={activeImage.url} /> : null
                    }
                </Modal.Body>
            </Modal>

        </div>
    );
};